import React, { useState, useEffect, useCallback, useRef } from "react";
import styles from "./AppSumoRedeem.module.css";
import { previewAppSumoCode, redeemAppSumoCode } from "../../Store/apiStore";
import AnimatedButton from "../AnimatedButton/AnimatedButton";


// The tier lives inddd utm_campaign, saved to localStorage by useUTMParams during
// signup — by the time this screen renders, the query string is long gone.
const readUtmCampaign = () => {
  const fromUrl = new URLSearchParams(window.location.search).get(
    "utm_campaign",
  );
  if (fromUrl) return fromUrl;
  try {
    const stored = JSON.parse(localStorage.getItem("utm_data") || "null");
    return stored?.utm_campaign || null;
  } catch {
    return null;
  }
};

const readCodeFromUrl = () =>
  new URLSearchParams(window.location.search).get("code") || "";

const SUPPORT_EMAIL = "support@rxpt.us";
const SUPPORT_SUBJECT = "AppSumo code redemption";
const SUPPORT_MAILTO = `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(
  SUPPORT_SUBJECT,
)}`;
// Gmail's compose URL is the fallback when mailto: has no handler. Confirmed
// on this project's own Windows box: Default apps → Mail lists only a Store
// package under "URL:p9wzdncrfj41q" with no MAILTO protocol registered, so
// every mailto: click there is swallowed in silence. Users redeeming an
// AppSumo code cannot skip this screen, so a dead support link strands them.
const SUPPORT_WEBMAIL =
  "https://mail.google.com/mail/?view=cm&fs=1" +
  `&to=${encodeURIComponent(SUPPORT_EMAIL)}` +
  `&su=${encodeURIComponent(SUPPORT_SUBJECT)}`;

// Inline SVGs rather than an icon package: this screen is on the signup path,
// where an extra dependency would be loaded by every visitor, AppSumo or not.
const svg = (props) => ({
  width: 18,
  height: 18,
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 1.8,
  strokeLinecap: "round",
  strokeLinejoin: "round",
  ...props,
});

const GiftIcon = () => (
  <svg {...svg({ width: 20, height: 20 })}>
    <rect x="2" y="7" width="20" height="5" rx="1" />
    <path d="M4 12v9h16v-9M12 7v14" />
    <path d="M12 7S9.5 3 7.5 3a2.5 2.5 0 0 0 0 5M12 7s2.5-4 4.5-4a2.5 2.5 0 0 1 0 5" />
  </svg>
);

const BoltIcon = () => (
  <svg {...svg({ width: 20, height: 20 })} fill="currentColor" stroke="none">
    <path d="M13.4 2.5 5.6 13.1a.6.6 0 0 0 .5 1h4.2l-1.1 6.9a.6.6 0 0 0 1.1.4l7.9-10.6a.6.6 0 0 0-.5-1h-4.2l1.1-6.9a.6.6 0 0 0-1.1-.4z" />
  </svg>
);

const ArrowIcon = () => (
  <svg {...svg({ width: 19, height: 19, strokeWidth: 2 })}>
    <path d="M4.5 12h14M13 6.5l5.5 5.5-5.5 5.5" />
  </svg>
);

const CheckCircleIcon = () => (
  <svg {...svg({ width: 22, height: 22 })}>
    <circle cx="12" cy="12" r="9" />
    <path d="M8.5 12.5l2.5 2.5 4.5-5" />
  </svg>
);

const ShieldIcon = () => (
  <svg {...svg({ width: 15, height: 15 })}>
    <path d="M12 3l7 3v5.5c0 4-2.9 7.6-7 9.5-4.1-1.9-7-5.5-7-9.5V6z" />
    <path d="M9.2 12.2l2 2 3.6-4" />
  </svg>
);

const HeadsetIcon = () => (
  <svg {...svg({ width: 15, height: 15 })}>
    <path d="M4.5 14v-2a7.5 7.5 0 0 1 15 0v2" />
    <rect x="2.8" y="13.2" width="4" height="6" rx="2" />
    <rect x="17.2" y="13.2" width="4" height="6" rx="2" />
    <path d="M19.5 19.2a3 3 0 0 1-3 2.3h-2" />
  </svg>
);

// The gift illustration is drawn rather than shipped as an asset: it is the
// only image on the screen, and an inline SVG cannot arrive late or 404 on a
// signup step the user has no way to skip. The motion lives in the stylesheet
// so a reduced-motion preference can switch all of it off in one place.
const Sparkle = ({ x, y, s = 1, className }) => (
  <path
    className={className}
    d={`M${x} ${y - 5 * s}q${0.9 * s} ${3.6 * s} ${4.4 * s} ${5 * s}q${-3.5 * s} ${1.4 * s} ${-4.4 * s} ${5 * s}q${-0.9 * s} ${-3.6 * s} ${-4.4 * s} ${-5 * s}q${3.5 * s} ${-1.4 * s} ${4.4 * s} ${-5 * s}z`}
    fill="#6524eb"
  />
);

const GiftArt = () => (
  <svg viewBox="0 0 200 190" aria-hidden="true" focusable="false">
    <defs>
      <linearGradient id="asGiftBody" x1="0.1" y1="0" x2="0.95" y2="1">
        <stop offset="0%" stopColor="#a67dff" />
        <stop offset="55%" stopColor="#7c3aed" />
        <stop offset="100%" stopColor="#5312c9" />
      </linearGradient>
      <linearGradient id="asGiftLid" x1="0.05" y1="0" x2="0.95" y2="1">
        <stop offset="0%" stopColor="#c9adff" />
        <stop offset="55%" stopColor="#8b47f7" />
        <stop offset="100%" stopColor="#6320d8" />
      </linearGradient>
      <linearGradient id="asGiftRibbon" x1="0" y1="0" x2="0.3" y2="1">
        <stop offset="0%" stopColor="#ffffff" />
        <stop offset="100%" stopColor="#d9c8ff" />
      </linearGradient>
      <radialGradient id="asGiftGlow">
        <stop offset="0%" stopColor="#a374ff" stopOpacity="0.42" />
        <stop offset="65%" stopColor="#a374ff" stopOpacity="0.12" />
        <stop offset="100%" stopColor="#a374ff" stopOpacity="0" />
      </radialGradient>
    </defs>

    <circle className={styles.giftGlow} cx="100" cy="100" r="82" fill="url(#asGiftGlow)" />
    <ellipse className={styles.giftShadow} cx="100" cy="170" rx="50" ry="8" />

    <g className={styles.giftFloat}>
      {/* Box: front face, then a darker right face so it reads as a solid
          object rather than a flat rectangle. */}
      <rect x="50" y="88" width="100" height="74" rx="9" fill="url(#asGiftBody)" />
      <path d="M141 88h-9v74h9a9 9 0 0 0 9-9V97a9 9 0 0 0-9-9z" fill="#4c11bd" opacity="0.35" />
      <rect x="50" y="88" width="100" height="74" rx="9" fill="none" stroke="#4a11b5" strokeOpacity="0.25" strokeWidth="1.5" />

      {/* Ribbon down the front, and the lid sitting on top of it. */}
      <rect x="91" y="88" width="18" height="74" fill="url(#asGiftRibbon)" />
      <rect x="40" y="64" width="120" height="30" rx="10" fill="url(#asGiftLid)" />
      <rect x="40" y="64" width="120" height="30" rx="10" fill="none" stroke="#4a11b5" strokeOpacity="0.22" strokeWidth="1.5" />
      <rect x="91" y="64" width="18" height="30" fill="url(#asGiftRibbon)" />
      <path d="M50 71h100" stroke="#ffffff" strokeOpacity="0.5" strokeWidth="3" strokeLinecap="round" />

      {/* Bow. */}
      <g className={styles.giftBow}>
        <path
          d="M100 64C83 64 70 56 70 45.5 70 37 80.5 34 88 41c6.4 6 10.4 14 12 23z"
          fill="url(#asGiftRibbon)"
          stroke="#7c4ae0"
          strokeOpacity="0.22"
          strokeWidth="1.4"
        />
        <path
          d="M100 64c17 0 30-8 30-18.5 0-8.5-10.5-11.5-18-4.5-6.4 6-10.4 14-12 23z"
          fill="url(#asGiftRibbon)"
          stroke="#7c4ae0"
          strokeOpacity="0.22"
          strokeWidth="1.4"
        />
        <ellipse cx="100" cy="63" rx="10.5" ry="8.5" fill="#f4ecff" stroke="#7c4ae0" strokeOpacity="0.22" strokeWidth="1.4" />
      </g>
    </g>

    <g className={styles.giftSparkles}>
      <Sparkle x={170} y={36} s={1.15} className={styles.sparkA} />
      <Sparkle x={178} y={72} s={0.8} className={styles.sparkB} />
      <Sparkle x={28} y={50} s={0.9} className={styles.sparkC} />
      <circle className={styles.sparkB} cx="150" cy="22" r="3.6" fill="#a78bfa" />
      <circle className={styles.sparkC} cx="22" cy="104" r="3.2" fill="#a78bfa" />
      <circle className={styles.sparkA} cx="184" cy="114" r="2.8" fill="#a78bfa" />
    </g>
  </svg>
);

const NEXT_STEPS = [
  {
    title: "Create your AI receptionist",
    text: "Design how your receptionist answers calls and helps your customers.",
  },
  {
    title: "Complete a quick business setup",
    text: "Add a few details so your receptionist can represent your business.",
  },
  {
    title: "See your included minutes",
    text: "Your AppSumo minutes will be applied to your account automatically.",
  },
];

/**
 * The redeem step shown after OTP when the signup carried an AppSumo campaign.
 *
 * It does not decide where the user goes next: onDone() hands control back to
 * the caller, which continues the normal post-OTP routing (business step, or
 * wherever that user was headed).
 */
const AppSumoRedeem = ({ onDone }) => {
  const [code, setCode] = useState(readCodeFromUrl);
  const [utmCampaign] = useState(readUtmCampaign);
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState("");
  const [checking, setChecking] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Guards a stale preview response from overwriting a newer one.
  const previewSeq = useRef(0);

  const check = useCallback(
    async (raw) => {
      const value = String(raw || "")
        .trim()
        .toUpperCase();
      const seq = ++previewSeq.current;

      if (value.length < 4) {
        setPreview(null);
        setError("");
        return;
      }

      setChecking(true);
      try {
        const data = await previewAppSumoCode(value, utmCampaign);
        if (seq !== previewSeq.current) return;
        setPreview(data?.valid ? data : null);
        setError("");
      } catch (err) {
        if (seq !== previewSeq.current) return;
        setPreview(null);
        const reason = err?.response?.data?.reason;
        if (reason === "not_found") setError("We could not find that code.");
        else if (reason === "already_redeemed")
          setError("This code has already been used.");
        else if (reason === "void")
          setError("This code is no longer valid. Contact support.");
        else if (reason === "invalid_format") setError("");
        else setError("");
      } finally {
        if (seq === previewSeq.current) setChecking(false);
      }
    },
    [utmCampaign],
  );

  // Debounced so typing does not fire a request per keystroke.
  useEffect(() => {
    const id = setTimeout(() => check(code), 450);
    return () => clearTimeout(id);
  }, [code, check]);

  // Gmail compose opens straight away on click, no mailto: attempt first.
  //
  // The earlier version tried mailto: and only fell back after an 800ms grace
  // period, which read as a broken link: on a machine with no MAILTO handler
  // (Windows registers none by default) the first click appeared to do nothing
  // for most of a second, so people clicked again before the fallback fired.
  //
  // Opening the compose window immediately is the predictable behaviour. The
  // href stays a real mailto: so middle-click, "Copy email address" and any
  // properly configured desktop client still work off the anchor itself.
  const handleSupportClick = (e) => {
    e.preventDefault();
    window.open(SUPPORT_WEBMAIL, "_blank", "noopener,noreferrer");
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (submitting) return;

    const value = code.trim().toUpperCase();
    if (value.length < 4) {
      setError("Enter the code from your AppSumo purchase.");
      return;
    }

    setSubmitting(true);
    setError("");
    try {
      const data = await redeemAppSumoCode(value, utmCampaign);

      // The durable AppSumo marker on this browser. isPythonBackend() reads it
      // to route every later agent action to the Python backend, where the
      // credit pools this grant just funded actually live.
      if (data?.tier) {
        localStorage.setItem("appsumo_tier_level", String(data.tier));
      }

      // Straight on to setup rather than a confirmation screen of its own. The
      // grant is worth announcing, but not worth a page the user has to read
      // and dismiss before starting — the next screen says it in a popup while
      // they are already getting on with the thing they came to do.
      onDone?.(data);
    } catch (err) {
      const d = err?.response?.data;
      const messages = {
        not_found: "We could not find that code. Check it and try again.",
        already_redeemed: "This code has already been used.",
        already_redeemed_by_you: "You have already redeemed this code.",
        account_already_redeemed:
          "This account has already redeemed an AppSumo code.",
        void: "This code is no longer valid. Please contact support.",
        tier_unreadable:
          "This link is missing its plan. Please open the redeem link from AppSumo.",
        invalid_format: "That does not look like a valid code.",
      };
      setError(
        d?.message ||
          messages[d?.reason] ||
          "Something went wrong. Please try again.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  const codeIsValid = !!(preview?.valid && preview.minutes);

  return (
    <div className={styles.wrap}>
      <div className={styles.stack}>
        <header className={styles.hero}>
          <div className={styles.heroCopy}>
            <h1 className={styles.title}>
              <span className={styles.titleAccent}>Redeem</span> your
              <br />
              AppSumo offer
            </h1>
            <p className={styles.subtitle}>
              Enter your redemption code to unlock your offer and get started.
            </p>
          </div>
          <div className={styles.heroArt} aria-hidden="true">
            <GiftArt />
          </div>
        </header>

        <form className={styles.form} onSubmit={handleSubmit}>
          <label className={styles.label} htmlFor="appsumo-code">
            Redemption code
          </label>
          <div className={styles.inputWrap}>
            <input
              id="appsumo-code"
              className={`${styles.input} ${error ? styles.err : ""} ${
                codeIsValid ? styles.ok : ""
              }`}
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder="AS-RXP-XXXX-XXXX"
              autoComplete="off"
              autoFocus
              disabled={submitting}
            />
            {/* The tick doubles as the "code recognised" signal, so the tier
                and minutes below it are confirmation rather than the only cue. */}
            {codeIsValid ? (
              <span className={styles.inputTick} aria-hidden="true">
                <CheckCircleIcon />
              </span>
            ) : null}
          </div>

          {codeIsValid ? (
            <div className={styles.preview}>
              <span className={styles.previewIcon} aria-hidden="true">
                <BoltIcon />
              </span>
              <div className={styles.previewCopy}>
                <span className={styles.previewTier}>{preview.planName}</span>
                <p className={styles.previewNote}>
                  One-time minutes, yours for life. They never expire.
                </p>
              </div>
              <div className={styles.previewMinsBox}>
                <span className={styles.previewMins}>{preview.minutes}</span>
                <span className={styles.previewMinsUnit}>min</span>
                <span className={styles.previewMinsTag}>ONE-TIME</span>
              </div>
            </div>
          ) : null}

          {error ? <div className={styles.error}>{error}</div> : null}

          {/* The app's standard CTA. It renders a div rather than a <button>,
              so the form keeps its own onSubmit — that is what still handles
              Enter from the code field. */}
          <div className={styles.submit}>
            <AnimatedButton
              label={
                <span className={styles.btnLabel}>
                  <GiftIcon />
                  Redeem code
                  <ArrowIcon />
                </span>
              }
              onClick={handleSubmit}
              isLoading={submitting}
              disabled={submitting || checking || code.trim().length < 4}
              positionType="relative"
              width="100%"
              bottom="0"
            />
          </div>

          {/* The address is spelled out, not hidden behind "Contact support",
              so it stays readable even when the click does nothing visible —
              see handleSupportClick for why it is also copied. */}
          <p className={styles.help}>
            <span className={styles.helpIcon} aria-hidden="true">
              <HeadsetIcon />
            </span>
            Need help?{" "}
            <a
              className={styles.helpLink}
              href={SUPPORT_MAILTO}
              onClick={handleSupportClick}
            >
              {SUPPORT_EMAIL}
            </a>
          </p>

          {/* No way past this screen without redeeming. The whole account is
              the code — its tier decides the minutes, the number limit and the
              plan — so letting someone skip would leave them on a free agent
              they did not buy. Anyone who cannot find their code has the
              support link above. */}
        </form>

        <section className={styles.next}>
          <p className={styles.nextKicker}>What happens next</p>
          <h2 className={styles.nextTitle}>Get started in 3 simple steps</h2>

          <ol className={styles.steps}>
            {NEXT_STEPS.map((step, i) => (
              <li className={styles.step} key={step.title}>
                <span className={styles.stepNum}>{i + 1}</span>
                <div className={styles.stepCopy}>
                  <span className={styles.stepTitle}>{step.title}</span>
                  <span className={styles.stepText}>{step.text}</span>
                </div>
              </li>
            ))}
          </ol>
        </section>

        <p className={styles.secure}>
          <ShieldIcon />
          Secure redemption. Your data is safe with us.
        </p>
      </div>
    </div>
  );
};

export default AppSumoRedeem;
